// Assistant data lives only in the principal-scoped QueryClient. Bound dormant
// entries as well as each chat's page window; never persist message payloads.
export const ASSISTANT_CACHE_MS = 5 * 60 * 1000;
export const HISTORY_MAX_PAGES = 8;
export const HISTORY_PAGE_SIZE = 25;
export const ASSISTANT_CACHE_LIMITS = { conversation: 8, widget: 48, details: 8 };

const installed = new WeakSet();

export function boundAssistantCache(client) {
  if (installed.has(client)) return;
  installed.add(client);
  let pruning = false;
  const touched = new WeakMap();
  let sequence = 0;
  client.getQueryCache().subscribe((event) => {
    if (event.type === 'observerAdded' || event.type === 'observerRemoved' || (event.type === 'updated' && event.action?.type === 'success')) touched.set(event.query, ++sequence);
    if (pruning || !['updated', 'observerRemoved'].includes(event.type)) return;
    const kind = event.query?.queryKey?.[1];
    const limit = ASSISTANT_CACHE_LIMITS[kind];
    if (event.query?.queryKey?.[0] !== 'assistant' || !limit) return;
    const inactive = client.getQueryCache().findAll({ queryKey: ['assistant', kind] })
      .filter((query) => query.getObserversCount() === 0)
      .sort((a, b) => (touched.get(b) || 0) - (touched.get(a) || 0));
    pruning = true;
    try {
      inactive.slice(limit).forEach((query) => client.getQueryCache().remove(query));
    } finally { pruning = false; }
  });
}

/** Preserve chronology and metadata while pages overlap after a refresh. */
export function flattenHistory(data) {
  if (!data?.pages?.length) return undefined;
  const messages = new Map();
  data.pages.forEach((page) => page.messages?.forEach((message) => messages.set(message.id, message)));
  const latest = data.pages[data.pages.length - 1];
  return {
    ...latest,
    messages: [...messages.values()],
    next_before: data.pages[0].next_before ?? null,
    next_after: latest.next_after ?? null,
  };
}

/** Refresh the tail without losing an older reader's contiguous window. */
export function mergeLatestHistory(existing, latest) {
  const previous = flattenHistory(existing);
  if (!previous?.messages?.length) return { pages: [latest], pageParams: [{}] };
  const start = previous.messages.findIndex((message) => message.id === latest.messages?.[0]?.id);
  const overlap = start >= 0;
  const revisions = new Set(latest.messages?.map((message) => message.content?.dashboard_revision_of).filter(Boolean));
  const combined = overlap
    ? [...previous.messages.slice(0, start), ...latest.messages]
    : previous.messages;
  const trim = Math.max(0, combined.length - HISTORY_MAX_PAGES * HISTORY_PAGE_SIZE);
  const messages = combined.slice(trim).map((message) => {
    if (!revisions.has(message.id)) return message;
    const { dashboard, widgets, ...content } = message.content || {};
    return { ...message, content, superseded: true, has_dashboard: Boolean(message.has_dashboard || dashboard), dashboard_title: message.dashboard_title || dashboard?.title };
  });
  const moreBefore = Boolean(trim || previous.next_before);
  const moreAfter = overlap ? latest.next_after : (previous.next_after || previous.messages.at(-1)?.id);
  const pages = [];
  const pageParams = [];
  for (let index = 0; index < messages.length; index += HISTORY_PAGE_SIZE) {
    const chunk = messages.slice(index, index + HISTORY_PAGE_SIZE);
    pages.push({
      ...latest, messages: chunk,
      next_before: index > 0 || moreBefore ? chunk[0].id : null,
      next_after: index + chunk.length < messages.length ? chunk.at(-1).id : moreAfter,
    });
    pageParams.push(index === 0
      ? (messages.length <= HISTORY_PAGE_SIZE && overlap ? {} : { around: chunk[Math.floor(chunk.length / 2)].id })
      : { after: messages[index - 1].id });
  }
  return { pages, pageParams };
}
